import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserProfile } from '../../types/auth';
import type { Doctor } from '../../types/users';
import { LoginResponse } from '@/services/authService';
import { doctorService } from '../../services/doctorService';

// Keys for localStorage
const AUTH_TOKEN_KEY = 'auth_token';

// 1. Define what we store
interface AuthState {
  user: UserProfile | null; // Can hold full Patient/Doctor details now
  token: string | null;
}

// 2. Initialize State (Read Token Immediately)
const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);

const initialState: AuthState = {
  user: null,
  token: storedToken,
};

// 3. Async Thunks
export const updateDoctorProfile = createAsyncThunk(
  'auth/updateDoctorProfile',
  async (updates: Partial<Doctor>) => {
    return doctorService.updateDoctorProfile('', updates);
  }
);

// 4. Create Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Action: Login Successful (Save Data)
    login: (state, action: PayloadAction<LoginResponse>) => {
      // Store the COMPLETE profile returned from backend
      state.user = action.payload.user;
      state.token = action.payload.accessToken;
      localStorage.setItem(AUTH_TOKEN_KEY, action.payload.accessToken);
    },

    // Action: Update user (e.g. after avatar upload)
    setUser: (state, action: PayloadAction<UserProfile>) => {
      state.user = action.payload;
    },

    // Action: Logout
    logout: state => {
      state.user = null;
      state.token = null;
      localStorage.removeItem(AUTH_TOKEN_KEY);
    },
  },
  extraReducers: builder => {
    builder.addCase(updateDoctorProfile.fulfilled, (state, action) => {
      state.user = action.payload;
    });
  },
});

export const { login, logout, setUser } = authSlice.actions;

// Selector to derive 'isAuthenticated' dynamically
export const selectIsAuthenticated = (state: { auth: AuthState }) => !!state.auth.token;

export default authSlice.reducer;
